import { formatDistanceToNowStrict, parseISO } from 'date-fns';
import { he as heDateLocale } from 'date-fns/locale';
import type { Conversation, PendingEmailDraft } from '@/types';
import type { Lang } from '@/stores/langStore';

/**
 * OP-582 — shared vocabulary for "a bot-drafted email reply is waiting for a human".
 *
 * <p>Four surfaces answer that question (the review card, the conversation row, the inbox
 * queue block, and the rail badge). They live in one module because the failure this closes
 * was surfaces DISAGREEING: the card read a countdown off `maxWaitAtUtc` and announced a send,
 * while nothing else on screen mentioned the draft at all — so a held reply was simultaneously
 * "sending now" and invisible.</p>
 */

/**
 * True when NO elapsed clock can release this draft — the tenant turned
 * `Email.DraftReview.AutoSendEnabled` off (OP-578), or the channel is in Consultation Only
 * mode (OP-579). Such a draft reaches the customer ONLY if a human approves, edits or revises it.
 *
 * <p>Deliberately an <b>exact `=== false`</b> test. `undefined` means the field was not on the
 * wire — a service build that predates OP-582 — and must read as "auto-send on", the behaviour
 * every existing tenant already has. A falsy check would invent a hold the server never
 * declared and tell an agent a reply is stuck when it is merely on a timer.</p>
 */
export function isDraftHeld(draft: Pick<PendingEmailDraft, 'autoSendEnabled'>): boolean {
  return draft.autoSendEnabled === false;
}

/**
 * How long this draft has been waiting, as a localized bare duration ("3 hours", "20 minutes").
 * Returns `null` for an unparseable timestamp so callers render nothing rather than "NaN".
 *
 * <p>Age, not deadline. `createdAtUtc` has ridden on the draft contract since ADR-019 and was
 * never rendered anywhere, so nothing distinguished a draft raised a minute ago from one raised
 * yesterday — and on a held draft, which has no meaningful deadline, age is the only signal
 * that a customer is being left unanswered.</p>
 */
export function formatDraftAge(createdAtUtc: string, lang: Lang): string | null {
  const created = parseISO(createdAtUtc);
  if (Number.isNaN(created.getTime())) return null;
  return formatDistanceToNowStrict(created, { locale: lang === 'he' ? heDateLocale : undefined });
}

/**
 * Every conversation carrying a draft awaiting review, OLDEST FIRST.
 *
 * <p>Oldest first on purpose: the ordering answers "who has been ignored longest", which is the
 * only question this queue exists to answer. Newest-first would bury exactly the draft that
 * most needs a person.</p>
 */
export function conversationsAwaitingDraftReview(conversations: Conversation[]): Conversation[] {
  return (Array.isArray(conversations) ? conversations : [])
    .filter((c) => !!c.pendingDraft)
    .sort((a, b) => {
      const at = Date.parse(a.pendingDraft!.createdAtUtc);
      const bt = Date.parse(b.pendingDraft!.createdAtUtc);
      // An unparseable timestamp sorts last rather than poisoning the comparator.
      if (Number.isNaN(at)) return Number.isNaN(bt) ? 0 : 1;
      if (Number.isNaN(bt)) return -1;
      return at - bt;
    });
}
